
import { useState, useEffect } from 'react';
import { supabase } from "@/integrations/supabase/client";

interface UseLeaderboardDataProps {
  selectedRegion: string;
  selectedCountry: string;
  selectedAccount: string;
  selectedProduct: string;
  selectedIndustry: string;
  filterType: string;
  isVolumeManuallyAdjusted: boolean;
  setOrderVolume: (value: number) => void;
  setVolumeInputValue: (value: string) => void;
  setPricePerUnit: (value: number) => void;
  setPriceInputValue: (value: string) => void;
  setDiscountRate: (value: number) => void;
  setDiscountInputValue: (value: string) => void;
  setCostPerUnit: (value: number) => void;
  setCostInputValue: (value: string) => void;
  setCostPerUnitRange: (range: { min: number; max: number }) => void;
  setPricePerUnitRange: (range: { min: number; max: number }) => void;
  setOrderVolumeRange: (range: { min: number; max: number }) => void;
  setActualRevenue: (value: number) => void;
  setCogs: (value: number) => void;
}

export const useLeaderboardData = (props: UseLeaderboardDataProps) => {
  const [leaderboardData, setLeaderboardData] = useState<any[]>([]);

  const fetchLeaderboardData = async () => {
    try {
      let query = supabase.from('leaderboard').select('*');
      if (props.selectedRegion !== "all") {
        query = query.eq('region', props.selectedRegion);
      }
      if (props.selectedCountry !== "all") {
        query = query.eq('country', props.selectedCountry);
      }
      if (props.selectedAccount !== "all") {
        query = query.eq('account_name', props.selectedAccount);
      }
      if (props.filterType === "product" && props.selectedProduct !== "all") {
        query = query.eq('product_line', props.selectedProduct);
      } else if (props.filterType === "industry" && props.selectedIndustry !== "all") {
        query = query.eq('industry', props.selectedIndustry);
      }
      const { data, error } = await query;
      if (error) {
        console.error('Error fetching leaderboard data:', error);
        return;
      }
      if (data) {
        setLeaderboardData(data);
        const totalAchievement = data.reduce((sum, item) => sum + (item.achievement || 0), 0);
        const totalCogs = data.reduce((sum, item) => sum + (item.cogs || 0), 0);

        props.setActualRevenue(totalAchievement);
        props.setCogs(totalCogs);

        const recordCount = data.length;
        
        if (!props.isVolumeManuallyAdjusted) {
          props.setOrderVolume(recordCount);
          props.setVolumeInputValue(recordCount.toString());
        }
        
        const avgPrice = totalAchievement / (recordCount || 1);
        const roundedPrice = Math.round(avgPrice * 100) / 100;
        props.setPricePerUnit(roundedPrice);
        props.setPriceInputValue(roundedPrice.toString());

        props.setDiscountRate(0);
        props.setDiscountInputValue("0");

        if (recordCount > 0 && !props.isVolumeManuallyAdjusted) {
          const calculatedCostPerUnit = totalCogs / recordCount;
          const roundedCostPerUnit = Math.round(calculatedCostPerUnit * 100) / 100;
          props.setCostPerUnit(roundedCostPerUnit);
          props.setCostInputValue(roundedCostPerUnit.toString());

          const costBuffer = roundedCostPerUnit * 0.5;
          props.setCostPerUnitRange({
            min: Math.max(1, roundedCostPerUnit - costBuffer),
            max: roundedCostPerUnit + costBuffer
          });
        }
        
        const priceBuffer = roundedPrice;
        const newPriceMin = Math.max(1, roundedPrice - priceBuffer);
        const newPriceMax = roundedPrice + priceBuffer;
        props.setPricePerUnitRange({
          min: newPriceMin,
          max: newPriceMax
        });
        
        const volumeBuffer = recordCount;
        const newVolumeMin = Math.max(1, recordCount - volumeBuffer);
        const newVolumeMax = recordCount + volumeBuffer;
        props.setOrderVolumeRange({
          min: newVolumeMin,
          max: newVolumeMax
        });
      }
    } catch (error) {
      console.error('Error fetching leaderboard data:', error);
    }
  };

  return {
    leaderboardData,
    fetchLeaderboardData
  };
};
